import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, Clock, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GoalReminders = ({ user }) => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGoals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGoals = async () => {
    try {
      const res = await fetch(`${API_URL}/api/goals`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGoals(data.filter(g => g.status === 'active'));
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const getDaysRemaining = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyLevel = (daysRemaining) => {
    if (daysRemaining < 0) return 'overdue';
    if (daysRemaining <= 3) return 'urgent';
    if (daysRemaining <= 7) return 'soon';
    return 'normal';
  };

  if (loading || goals.length === 0) return null;

  // Sort by deadline (closest first)
  const sortedGoals = [...goals].sort((a, b) => 
    new Date(a.deadline) - new Date(b.deadline)
  );

  // Get goals that need attention (within 7 days or overdue)
  const urgentGoals = sortedGoals.filter(g => getDaysRemaining(g.deadline) <= 7);

  if (urgentGoals.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4"
    >
      <Card className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 rounded-2xl">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-5 h-5 text-amber-600" />
          <h3 className="font-semibold text-amber-900">Goal Deadlines Coming Up</h3>
        </div>

        <div className="space-y-2">
          {urgentGoals.slice(0, 3).map((goal) => {
            const daysRemaining = getDaysRemaining(goal.deadline);
            const urgency = getUrgencyLevel(daysRemaining);

            return (
              <div
                key={goal.id}
                onClick={() => navigate('/goals')}
                className="flex items-center justify-between bg-white rounded-xl p-3 cursor-pointer hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3">
                  {urgency === 'overdue' ? (
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  ) : urgency === 'urgent' ? (
                    <Clock className="w-5 h-5 text-orange-500" />
                  ) : (
                    <Target className="w-5 h-5 text-amber-500" />
                  )}
                  <div>
                    <p className="font-medium text-sm">{goal.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {goal.specific?.substring(0, 40)}...
                    </p>
                  </div>
                </div>

                <Badge
                  className={
                    urgency === 'overdue' ? 'bg-red-100 text-red-800' :
                    urgency === 'urgent' ? 'bg-orange-100 text-orange-800' :
                    'bg-amber-100 text-amber-800'
                  }
                >
                  {daysRemaining < 0 
                    ? `${Math.abs(daysRemaining)}d overdue` 
                    : daysRemaining === 0 
                      ? 'Due today!' 
                      : `${daysRemaining}d left`
                  }
                </Badge>
              </div>
            );
          })}
        </div>

        {urgentGoals.length > 3 && (
          <button
            onClick={() => navigate('/goals')}
            className="w-full mt-3 text-sm text-amber-700 hover:text-amber-900 flex items-center justify-center gap-1"
          >
            View all {goals.length} goals <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </Card>
    </motion.div>
  );
};

export default GoalReminders;
